import { useUser } from "@/hooks/useUser";
import { useAppSession } from "@/contexts/appSession";
import { DetailedChatRoom } from "@/types/workspace";
import { ReactNode, createContext, useContext, useEffect, useMemo } from "react";

// User type from useUser hook
type UserType = ReturnType<typeof useUser>[0];

export type PermissionsContextApi = {
  user?: UserType
  status: "loading" | "authenticated" | "unauthenticated"
  /**
   * Check if user has permission at app level (default) or specific entity level.
   * @param permission - Permission key to check
   * @param workspaceId - Optional workspace ID to check workspace-level permission
   * @param orgId - Optional org ID to check org-level permission
   */
  has(permission: string, workspaceId?: string, orgId?: string): boolean;
}

export const PermissionsContext = createContext<PermissionsContextApi>(undefined!);
export const RoomPermissionsContext = createContext<PermissionsContextApi>(undefined!);

export const usePermissions = (opts?: {
  permissions?: string[],
  onFail?: () => void,
}) => {
  const permCtx = useContext(PermissionsContext);

  useEffect(() => {
    if ( permCtx.status === "authenticated" && permCtx.user ) {
      if ( opts?.permissions ) {
        for ( let perm of opts.permissions ) {
          if ( !permCtx.has(perm) ) {
            opts.onFail?.();
            return;
          }
        }
      }
    }
  }, [permCtx.user, permCtx.status])

  return permCtx
};

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const [user, status] = useUser();
  const { hasPermission } = useAppSession();

  const api: PermissionsContextApi = useMemo(() => ({
    user,
    status,
    has(permission: string, workspaceId?: string, orgId?: string) {
      if (status === "loading") {
        return false;
      }

      if (!user) {
        return false;
      }

      // Delegate to shared hasPermission from PermissionsStateContext
      return hasPermission(permission, workspaceId, orgId);
    }
  }), [user, status, hasPermission]);

  return (
    <PermissionsContext.Provider value={api}>
      {children}
    </PermissionsContext.Provider>
  )
}

export const useRoomPermissions = () => {
  return useContext(RoomPermissionsContext);
}

export type RoomPermissionProviderProps = {
  room: DetailedChatRoom
  children: ReactNode
}

/**
 * Check if a user has a permission in a room
 * @param room The room to check
 * @param user The user to check
 * @param permission The permission to check
 * @returns Whether or not the user has the permission
 */
export const hasRoomPermission = (room: DetailedChatRoom, user: NonNullable<UserType>, permission: string) => {
  if (room.ownerId === user.id) {
    return true;
  }

  if (room.details && room.details.permissions.includes(permission)) {
    return true;
  }

  return false;
}

export const RoomPermissionProvider = (props: RoomPermissionProviderProps) => {
  const { room } = props;
  const { user, status, has: hasAppPermission } = usePermissions();
  const { hasPermission } = useAppSession();

  const api: PermissionsContextApi = useMemo(() => ({
    user: user!,
    status,
    has(permission: string) {
      if (status === "loading") {
        return false;
      }

      if (!user) {
        return false;
      }

      // 1. Check app-level permissions (via parent context - includes wildcard)
      if (hasAppPermission(permission)) {
        return true;
      }

      // 2. Check workspace-level permissions (includes wildcard check)
      if (hasPermission(permission, room.id)) {
        return true;
      }

      // 3. Owner always has all permissions
      if (room.ownerId === user.id) {
        return true;
      }

      // 4. Legacy: check room.details.permissions (for backward compatibility)
      if (room.details && room.details.permissions.includes(permission)) {
        return true;
      }

      return false;
    }
  }), [user, status, hasAppPermission, hasPermission, room.id, room.ownerId, room.details]);

  return (
    <RoomPermissionsContext.Provider value={api}>
      {props.children}
    </RoomPermissionsContext.Provider>
  )
}